import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { SvgIconComponent } from '@mui/icons-material';
import { formatDollarAmount } from '../../utils/global/helper';
import { CheckoutSummaryItem } from '../../types/checkouts';

function LoadingContent({
  children,
  isLoading,
}: React.PropsWithChildren<{ isLoading?: boolean }>) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!isLoading) return <>{children}</>;

  return (
    <Stack direction="row" justifyContent="end" alignItems="center">
      <CircularProgress size={16} color="primary" />
    </Stack>
  );
}

function FloatingButton({
  onClick,
  icon,
}: {
  onClick: () => void;
  icon: SvgIconComponent;
}) {
  const theme = useTheme();

  const Icon = icon;

  return (
    <Box
      component="span"
      sx={{
        position: 'absolute',
        right: '-55px',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        top: 0,
      }}>
      <Button
        sx={{
          padding: 0,
          minWidth: 0,
        }}
        onClick={onClick}>
        <Icon htmlColor={theme.palette.outlineGrey.main} />
      </Button>
    </Box>
  );
}

function CellAction({
  children,
  onClick,
}: React.PropsWithChildren & {
  onClick: () => void;
}) {
  const theme = useTheme();
  return (
    <Button
      variant="text"
      sx={{
        padding: 0,
        minWidth: 0,
      }}
      onClick={onClick}>
      <Typography variant="body2" sx={{ color: theme.palette.info.main }}>
        {children}
      </Typography>
    </Button>
  );
}

const getLineItemText = (text: string | number, isLoading?: boolean) =>
  isLoading ? '-' : text;

interface Props {
  preSubtotalItems: CheckoutSummaryItem[];
  postSubtotalItems: CheckoutSummaryItem[];
  subtotal: number;
  total: number;
  minimumPrice?: {
    value: number;
    errorVisible?: boolean;
  };
  isLoading?: boolean;
}

function CheckoutFormPricingTable({
  preSubtotalItems,
  postSubtotalItems,
  subtotal,
  total,
  isLoading,
  minimumPrice,
}: Props) {
  const theme = useTheme();

  const topItems: CheckoutSummaryItem[] = [
    ...preSubtotalItems,
    {
      label: 'SUBTOTAL',
      value: subtotal,
      isSubtotal: true,
    },
  ];
  const bottomItems: CheckoutSummaryItem[] = [
    ...postSubtotalItems,
    {
      label: 'Total Due',
      value: total,
      isTotal: true,
    },
  ];
  const items = [...topItems, ...bottomItems];

  return (
    <Box
      className="pricing-summary-table"
      sx={{
        '& .MuiTableRow-root': {
          borderBottom: `1px solid ${theme.palette.lightGrey.main}`,
        },
        '& .MuiTableCell-body': {
          paddingX: 0,
          border: 'none',
        },
        maxWidth: '400px',
        border: `1px solid ${theme.palette.lightGrey.main}`,
        borderRadius: 1,
        padding: 3,
      }}>
      <Table>
        <TableBody>
          {items.map(item => {
            const isVisible = item.visible ?? true;
            if (!isVisible) return null;
            const isItemLoading = item.isLoading || isLoading;
            const isNegative = item.value < 0;
            return (
              <TableRow
                key={item.label}
                sx={{
                  borderBottom: item.isTotal ? 'none !important' : undefined,
                }}>
                {(!!item.value || !item.cellActionWhenEmpty) && (
                  <>
                    <TableCell sx={{ display: 'flex', gap: 1 }}>
                      <Typography
                        variant={
                          item.isSubtotal
                            ? 'overline'
                            : item.isTotal
                            ? 'h3'
                            : 'body2'
                        }>
                        {item.label}
                      </Typography>
                      {item.isPaid && (
                        <Typography
                          variant="body2"
                          style={{ marginRight: 5 }}
                          color="secondary">
                          (paid)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <LoadingContent isLoading={item.isTotal && isItemLoading}>
                        <Typography
                          variant={
                            item.isSubtotal
                              ? 'overline'
                              : item.isTotal
                              ? 'h3'
                              : 'body2'
                          }
                          style={{ textAlign: 'right' }}
                          color={
                            item.valueColor ||
                            (!item.isTotal && (isNegative || item.isPaid)
                              ? theme.palette.primary.main
                              : 'text.primary')
                          }>
                          {getLineItemText(
                            formatDollarAmount(item.value),
                            isItemLoading,
                          )}
                        </Typography>
                      </LoadingContent>
                    </TableCell>
                  </>
                )}
                {!item.value && item.cellActionWhenEmpty && (
                  <TableCell>
                    <CellAction onClick={item.cellActionWhenEmpty.onClick}>
                      {item.cellActionWhenEmpty.label}
                    </CellAction>
                  </TableCell>
                )}
                {!isItemLoading &&
                  item.floatingButton &&
                  (item.floatingButton.showWithNoValue || !!item.value) && (
                    <TableCell
                      sx={{
                        position: 'relative',
                        padding: 0,
                        border: 'none',
                        width: 0,
                      }}>
                      <FloatingButton
                        icon={item.floatingButton.icon}
                        onClick={item.floatingButton.onClick}
                      />
                    </TableCell>
                  )}
              </TableRow>
            );
          })}

          {minimumPrice && minimumPrice.errorVisible && (
            <TableRow sx={{ borderBottom: 'none !important' }}>
              <TableCell colSpan={2}>
                <FormHelperText
                  sx={{
                    textAlign: 'right',
                    width: '100%',
                    color: theme.palette.error.dark,
                  }}>
                  Enter a price greater than{' '}
                  {formatDollarAmount(minimumPrice.value)}
                </FormHelperText>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

export default CheckoutFormPricingTable;
